import { createSelector } from "reselect";

import { formatUTCToLocalDateString } from "lib/timeFormats";

import { getSideLoadedDataSets } from "./root";

const withoutCollision = (label: string, taken: Set<string>): string => {
  if (!taken.has(label)) {
    return label;
  }
  let attempt = 2;
  while (taken.has(`${label} #${attempt}`)) {
    attempt += 1;
  }
  return `${label} #${attempt}`;
};

// Names aren't unique - re-uploading a file makes a second data set with the
// same name - so a grid grouped on the name alone merges them into one row that
// no single action can target. Only the names that are actually shared get a
// date appended, so the common case reads as just the name.
export const selectSideLoadedDataSetLabelsById = createSelector(
  [getSideLoadedDataSets],
  (sideLoadedDataSets: Record<string, SideLoadedDataSet>) => {
    const dataSets = Object.values(sideLoadedDataSets).sort((a, b) =>
      a.created.localeCompare(b.created),
    );
    const sharedNames = new Set(
      dataSets
        .map(dataSet => dataSet.name)
        .filter((name, index, names) => names.indexOf(name) !== index),
    );

    const taken = new Set<string>();
    return dataSets.reduce<Record<string, string>>((labelsById, dataSet) => {
      const label = withoutCollision(
        sharedNames.has(dataSet.name)
          ? `${dataSet.name} (${formatUTCToLocalDateString(dataSet.created)})`
          : dataSet.name,
        taken,
      );
      taken.add(label);
      labelsById[dataSet.id] = label;
      return labelsById;
    }, {});
  },
);

export const selectSideLoadedDataSetsByLabel = createSelector(
  [getSideLoadedDataSets, selectSideLoadedDataSetLabelsById],
  (
    sideLoadedDataSets: Record<string, SideLoadedDataSet>,
    labelsById: Record<string, string>,
  ) =>
    Object.values(sideLoadedDataSets).reduce<Record<string, SideLoadedDataSet>>(
      (byLabel, dataSet) => {
        byLabel[labelsById[dataSet.id]] = dataSet;
        return byLabel;
      },
      {},
    ),
);
